import { type Database } from '@databases/model/database';
import { FieldType } from '@databases/model/field';
import { unparse } from 'papaparse';

import { type Warnings } from './warnings';

function serializeFieldType(fieldType: FieldType): string | undefined {
  switch (fieldType) {
    case FieldType.Text:
      return 'text';
    case FieldType.Number:
      return 'number';
    case FieldType.Date:
      return 'date';
    case FieldType.Select:
      return 'tag';
    case FieldType.User:
      return 'user';
    case FieldType.Attachment:
      return 'attachment';
    case FieldType.PageLink:
      return 'confluence-page-link';
    case FieldType.Hyperlink:
      return 'hyperlink';
    case FieldType.JiraIssue:
      return 'jira-work-item';
    case FieldType.JiraIssueDetails:
      return 'jira-work-item-details';
    case FieldType.PageStatus:
      return 'confluence-page-status';
    case FieldType.PageLabels:
      return 'confluence-page-labels';
    case FieldType.PageDetails:
      return 'confluence-page-details';
    default:
      return;
  }
}

/**
 * Serializes the schema of a database.
 *
 * This will serialize as a CSV with the following format:
 *
 * field_name,type,dependent_field
 * Field 1,text,
 * Field 2,number,
 * Field 3,date,
 * Field 4,tag,
 *
 * Where the dependent field is populated if the field has a dependency on another field
 *
 * @param database The database to serialize the schema of.
 * @returns The serialized schema.
 */
export function serializeDatabaseSchema(database: Database, warnings: Warnings): string {
  const schema = database.fields
    .map((fieldId) => {
      const field = database.fieldsById[fieldId];
      const type = serializeFieldType(field.type);

      if (!type) {
        warnings.add(`Unsupported field type: ${field.type} for field ${fieldId}`);
        return undefined;
      }

      return {
        field_name: field.name,
        type,
        dependent_field: '',
      };
    })
    .filter((e) => e !== undefined);

  return unparse(schema, {
    header: true,
    escapeChar: '\\',
    columns: ['field_name', 'type', 'dependent_field'],
  });
}
